import { apiFetch, cloudLogout, hasSession } from '../auth/account.js';
import { AUTH_GAST, merkeSitzung, setzeAuthPhase, setzeDatenPhase } from '../auth/phase.js';
import { resetAuthScreen, showVerifyBar, wireCloudAuth, zeigeAnbieter } from '../auth/screen.js';
import { dbDel, dbGet, dbPut, tx } from '../core/db.js';
import { loadAll, visCourses } from '../core/load.js';
import { resetUserState, state } from '../core/state.js';
import { $, esc } from '../core/text.js';
import { gOfflineGains } from '../game/equip.js';
import { G, gStats, gWeekAdvance, weekKey } from '../game/items.js';
import { gShowOffline } from '../game/screens.js';
import { advUpdateNav } from '../game/ui.js';
import { startTour } from '../onboarding/tour.js';
import { streakDays } from '../progress/streak.js';
import { checkAchievements, dailyBonus, levelInfo } from '../progress/xp.js';
import { refreshSocial, startSocialPoll } from '../social/api.js';
import { askUsernameModal, saveProfile } from '../social/profile.js';
import { seedDemoIfEmpty } from '../ui/modal.js';
import { refresh, setView } from '../ui/views.js';

/* ── Cloud-Sync: der GESAMTE Lernstand liegt (verschlüsselt) auf dem Konto ── */
export let cloudApplying = false, pushTimer = null;
export function collectCloudData() {
  return {
    courses: state.courses, decks: state.decks, cards: state.cards, log: state.log,
    progress: state.progress, profile: state.profile, goals: state.goals, calendar: state.cal,
  };
}
export async function applyCloudData(data) {
  cloudApplying = true;
  try {
    for (const s of ['courses', 'decks', 'cards', 'log']) await new Promise((r) => { tx(s, 'readwrite').clear().onsuccess = r; });
    for (const c of data.courses || []) await dbPut('courses', c);
    for (const d of data.decks || []) await dbPut('decks', d);
    for (const c of data.cards || []) await dbPut('cards', c);
    for (const l of data.log || []) await dbPut('log', l);
    if (data.progress) await dbPut('meta', { key: 'progress', value: data.progress });
    if (data.profile) await dbPut('meta', { key: 'profile', value: data.profile });
    if (data.goals) await dbPut('meta', { key: 'goals', value: data.goals });
    if (data.calendar) await dbPut('meta', { key: 'calendar', value: data.calendar });
    await loadAll();
  } finally { cloudApplying = false; }
}
export async function pullCloud() { return (await apiFetch('/data')).data; }
/* öffentliche Kennzahlen (unverschlüsselt) für Bestenliste & Profil-Popup */
export function collectPublicStats() {
  return {
    name: state.profile.name || '',
    level: levelInfo(state.progress.xp).level,
    xp: state.progress.xp || 0,
    rated: state.progress.rated || 0,
    streak: streakDays(),
    catColor: state.progress.catColor || 'orange',
    accessory: state.progress.equipped || '',
    bio: state.profile.bio || '',
    context: state.profile.context || '',
    school: state.profile.school || '',
    charLvl: G().charLvl || 1,
    maxSection: G().maxSection || 1,
    atkPower: Math.round(gStats().knowledge) || 0,
    weekKey: weekKey(),
    weekAdv: gWeekAdvance(),
  };
}
export async function pushCloud() {
  if (!hasSession()) return;
  try { await apiFetch('/data', { method: 'POST', body: JSON.stringify({ data: collectCloudData(), public: collectPublicStats() }) }); }
  catch { /* offline – nächster Change versucht es erneut */ }
}
export function schedulePush() { clearTimeout(pushTimer); pushTimer = setTimeout(pushCloud, 1500); }
export function syncTouch(store) { if (!cloudApplying) schedulePush(); }

/* Das frühere lokale Konto-System stand hier: Konten in IndexedDB, Passwörter
   als einfacher SHA-256 über Salt+Passwort, „angemeldet" hieß ein Eintrag in
   localStorage. Es ist ersatzlos entfernt. Ein zweites Auth-System ist immer
   das schwächere, und dieses war weder gehärtet noch gepflegt. */

export async function enterApp(acc) {
  const name = acc.username || (acc.email || '').split('@')[0];
  state.me = {
    id: acc.id, email: acc.email, username: acc.username || '',
    emailVerified: acc.emailVerified !== false,
    hasPassword: !!acc.hasPassword,
    providers: acc.providers || [],   // [{ provider, email }]
  };
  /* Ab hier ist das App-Gerüst zu sehen, aber noch nicht sein Inhalt.

     Der Lernstand liegt verschlüsselt auf dem Konto und kann den lokalen
     Stand ersetzen – was gerade in der Datenbank dieses Browsers liegt, kann
     also zu einem anderen Konto gehören. Es anzuzeigen und danach zu
     überschreiben wäre nicht nur unruhig, sondern schlicht falsch.

     Deshalb: Gerüst mit Skeletons (`laedt`), und erst wenn der Stand steht,
     die echten Zahlen (`bereit`). Das Umschalten passiert unten an genau
     einer Stelle – auch im Fehlerfall, sonst bliebe ein Skeleton für immer
     stehen und würde Inhalt versprechen, der nie kommt. */
  setzeDatenPhase('laedt');
  // Auch beim Start mit bestehender Sitzung daran erinnern, falls die Adresse
  // noch nicht bestätigt ist.
  showVerifyBar(acc);
  $('#account-line').textContent = `Angemeldet als ${name}${acc.email ? ' · ' + acc.email : ''}`;

  /* ── Kontowechsel: lokale Daten gehören immer genau einem Konto ─────────
     Ohne das hier trägt der Browser den Stand des vorherigen Kontos in das
     nächste hinein. Für ein leeres, frisches Konto ist das nicht nur
     kosmetisch: weiter unten läuft `pushCloud()`, und damit landen fremde
     Kurse, Karten und Fortschritte in der Cloud des neuen Kontos.

     Sichtbar wurde es am Abenteuer: ein Minuten altes Konto bekam eine
     Offline-Belohnung für „1 Stunde" – die Uhr (`lastTick`) stammte aus der
     Sitzung davor. Das war das harmlose Symptom eines Datenübertritts.

     Auf einem geteilten Rechner wäre das ein Datenschutzproblem. */
  const bisher = (await dbGet('meta', 'accountId'))?.value;
  if (bisher && bisher !== acc.id) {
    for (const s of ['courses', 'decks', 'cards', 'log']) {
      await new Promise((r) => { tx(s, 'readwrite').clear().onsuccess = r; });
    }
    for (const k of ['progress', 'profile', 'goals', 'calendar']) await dbDel('meta', k);
    localStorage.removeItem('demoSeeded');   // sonst bleibt das neue Konto leer
    resetUserState();
  }
  await dbPut('meta', { key: 'accountId', value: acc.id });

  /* Ab hier wird geladen. Das `finally` unten ist die Versicherung gegen den
     schlimmsten Ladezustand überhaupt: ein Skeleton, das nie verschwindet.
     Egal was hier schiefgeht – die Oberfläche zeigt danach echte Werte,
     notfalls den lokalen Stand. Lieber alte Zahlen als ein Versprechen, das
     nicht eingelöst wird. */
  try {
    try {
      const remote = await pullCloud();
      const hasRemote = remote && ((remote.courses && remote.courses.length) || (remote.cards && remote.cards.length) || (remote.progress && remote.progress.rated));
      if (hasRemote) await applyCloudData(remote);           // Konto ist die Quelle der Wahrheit
      else { await seedDemoIfEmpty(); await pushCloud(); }    // leeres Konto: lokalen/Demo-Stand hochladen
    } catch { await seedDemoIfEmpty(); }                      // offline: lokal weiterarbeiten
    /* Anzeigename aus dem NUTZERNAMEN ableiten, nicht aus der E-Mail.

       Vorher stand hier `acc.username || email.split('@')[0]`. Der zweite Teil
       ist der Haken: der Anzeigename steht öffentlich in der Bestenliste und
       im Profil. Bei einer Adresse wie vorname.nachname@… stand damit der
       echte Name jeder fremden Person auf dem Bildschirm, ohne dass ihn
       jemand dort hingeschrieben hätte.

       Ohne Nutzernamen bleibt das Feld jetzt leer; Profil und Bestenliste
       greifen dann ohnehin auf den Nutzernamen zurück. */
    if (!state.profile.name && acc.username) {
      state.profile.name = acc.username;
      state.profile.nameAuto = true;
      await saveProfile();
    }
    await dailyBonus();
    // soziale Erfolge + Anfrage-Punkt + öffentliche Stats hochladen
    await refreshSocial(); await checkAchievements(null, true); startSocialPoll(); pushCloud();
    const first = visCourses()[0];
    if (first && !state.selCourse) { state.selCourse = first.id; state.openCourses.add(first.id); }
  } finally {
    /* Erst rendern, dann freigeben. Andersherum wäre für einen Bildaufbau
       der leere Zustand zu sehen – Skeleton weg, Inhalt noch nicht da. */
    setView('dashboard');
    advUpdateNav();
    setzeDatenPhase('bereit');
  }
  /* Reihenfolge nach dem Eintreten – bewusst an EINER Stelle.

     Hier konkurrierten drei Dinge um denselben Bildschirm, jedes mit eigenem
     Zeitgeber: die Frage nach dem Nutzernamen (700 ms), die Onboarding-Tour
     (800 ms) und die Offline-Belohnung des Abenteuers (1100 ms). Die Tour
     liegt bei z-index 4000, das Fenster bei 90 – wer zuletzt kommt, gewinnt,
     und die Frage nach dem Namen war unter beidem begraben.

     Jetzt läuft es nacheinander: Name klären, dann Belohnung, dann Tour. */
  setTimeout(async () => {
    if (acc.frischRegistriert) {
      try { await askUsernameModal(state.me); }
      catch (err) {
        /* Nicht verschlucken. Beim ersten Anlauf blieb das Fenster aus und es
           gab keinerlei Spur – die Ursachensuche fing bei null an. */
        console.error('Nutzername-Fenster konnte nicht geöffnet werden:', err);
      }
    }
    // Wissensabenteuer: Offline-Sammlung beim Zurückkommen. Nicht direkt nach
    // einer Registrierung – da war noch niemand weg.
    if (!acc.frischRegistriert && G().on) {
      const off = gOfflineGains();
      if (off) gShowOffline(off);
    }
    startTour();   // zeigt sich nur beim allerersten Start
  }, 700);
}
export async function logout() {
  await pushCloud();          // letzten Stand sichern …
  await cloudLogout();        // … dann Sitzung serverseitig beenden
  $('#auth-status').textContent = '';
  $('#magic-sent')?.classList.add('hidden');
  ['#li-id', '#li-pass', '#re-user', '#re-email', '#re-pass', '#fg-email', '#np-pass'].forEach((s) => { if ($(s)) $(s).value = ''; });
  // Ohne das bleibt das zuletzt sichtbare Formular stehen – dann wurde beim
  // nächsten Anmelden die "Angemeldet bleiben"-Box des falschen Formulars gelesen.
  resetAuthScreen();
  /* Erst aufräumen, dann umschalten. Der Startbildschirm erscheint damit in
     dem Zustand, in dem er beim ersten Besuch stand – und nicht kurz mit dem
     Formular von vorhin. */
  setzeAuthPhase(AUTH_GAST);
  setzeDatenPhase('laedt');   // beim nächsten Anmelden wieder Skeletons statt alter Zahlen
  merkeSitzung(false);        // sonst startet der nächste Besuch mit der App-Hülle
  await zeigeAnbieter();
}
export function authMsg(msg, kind) { const el = $('#auth-status'); el.textContent = msg; el.className = 'auth-status ' + (kind || ''); }
export function wireAuth() {
  const lo = $('#btn-logout'); if (lo) lo.onclick = logout;
  const tb = $('#btn-tour'); if (tb) tb.onclick = () => { setView('dashboard'); setTimeout(() => startTour(true), 200); };

  // Kompletter Anmeldebildschirm (Passwort, Google, Apple, Reset) liegt in
  // auth/screen.js – hier nur noch der Anschluss an die App.
  wireCloudAuth(async (user) => { await enterApp(user); refresh(); })
    .catch((err) => authMsg(err.message || 'Anmeldung konnte nicht geladen werden.', 'err'));
}

export function fatal(msg) {
  document.body.innerHTML = `<div style="max-width:460px;margin:16vh auto;padding:30px;text-align:center;background:#fffaf0;border:4px solid #4a3f5c;border-radius:8px">
    <div style="font-size:2.4rem;margin-bottom:10px">🌸</div>
    <h2 style="font-size:1rem;margin-bottom:10px;font-family:'Press Start 2P',monospace">Kurz ein Problem</h2>
    <p style="color:#8a7f96;margin-bottom:18px;line-height:1.5">${esc(msg)}</p>
    <button onclick="location.reload()" style="background:#b79cf0;color:#fff;border:3px solid #4a3f5c;padding:12px 20px;border-radius:6px;font:inherit;cursor:pointer">Neu laden</button>
  </div>`;
}
